"use client"

import { useTransition } from "react"

import { setLang } from "@lib/i18n/actions"
import { useLang, useT } from "@lib/i18n/client"
import type { Lang } from "@lib/i18n/messages"

/* Flagi rysowane w SVG, nie emoji: emoji-flagi nie renderują się na Windowsie
   (Chrome/Edge pokazują wtedy litery "PL"/"GB" zamiast obrazka). */

const FlagPL = () => (
  <svg viewBox="0 0 20 14" className="h-3.5 w-5 rounded-[2px]" aria-hidden="true">
    <rect width="20" height="7" fill="#fff" />
    <rect y="7" width="20" height="7" fill="#dc143c" />
  </svg>
)

const FlagGB = () => (
  <svg viewBox="0 0 20 14" className="h-3.5 w-5 rounded-[2px]" aria-hidden="true">
    <rect width="20" height="14" fill="#012169" />
    <path d="M0 0 L20 14 M20 0 L0 14" stroke="#fff" strokeWidth="2.8" />
    <path d="M0 0 L20 14 M20 0 L0 14" stroke="#c8102e" strokeWidth="1.4" />
    <path d="M10 0 V14 M0 7 H20" stroke="#fff" strokeWidth="4.6" />
    <path d="M10 0 V14 M0 7 H20" stroke="#c8102e" strokeWidth="2.6" />
  </svg>
)

const OPTIONS: { lang: Lang; label: string; Flag: () => JSX.Element }[] = [
  { lang: "pl", label: "Zmień język na polski", Flag: FlagPL },
  { lang: "en", label: "Zmień język na angielski", Flag: FlagGB },
]

/**
 * Przełącznik języka przypięty w prawym dolnym rogu ekranu.
 *
 * Pokazuje obie flagi naraz, z podświetloną aktywną — przy dwóch językach to
 * czytelniejsze niż jedna flaga, przy której nie wiadomo, czy oznacza język
 * bieżący, czy ten, na który przełączy.
 */
const LanguageToggle = () => {
  const current = useLang()
  const t = useT()
  const [pending, startTransition] = useTransition()

  const choose = (lang: Lang) => {
    if (lang === current || pending) {
      return
    }
    startTransition(() => {
      setLang(lang)
    })
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] flex items-center gap-1 rounded-full border border-ui-border-base bg-[rgba(10,10,11,0.92)] p-1 shadow-lg backdrop-blur-sm"
      data-testid="language-toggle"
    >
      {OPTIONS.map(({ lang, label, Flag }) => {
        const active = lang === current
        return (
          <button
            key={lang}
            type="button"
            onClick={() => choose(lang)}
            aria-label={t(label)}
            title={t(label)}
            aria-pressed={active}
            disabled={pending}
            data-testid={`lang-${lang}`}
            className={
              "flex items-center justify-center rounded-full p-1.5 transition-opacity " +
              (active
                ? "opacity-100 ring-1 ring-[color:var(--ateisci-gold)]"
                : "opacity-45 hover:opacity-80")
            }
          >
            <Flag />
          </button>
        )
      })}
    </div>
  )
}

export default LanguageToggle
